import React, { useState } from 'react';
import { useBriefs } from 'features/briefs/hooks/useBriefs';
import ActionButton from 'shared/components/molecules/ActionButton';
import PromptButton from 'shared/components/molecules/PromptButton';
import { BRIEF_COPY } from 'shared/config/copy';
import { TRACKING } from 'shared/config/tracking';
import { UserBriefStatus } from 'shared/typings/userBriefs/enums';
import { trackEvent } from 'shared/utilities/trackingUtility';
import CreatorBriefStartingSteps from 'features/briefs/components/organisms/CreatorBriefStartingSteps';
import { useCurrentUser } from 'shared/hooks/useCurrentUser';
import { CreatorUser } from 'shared/typings/user';
import { noOp } from 'shared/defaults';
import { useNavigate } from 'react-router';
import { Brief } from 'shared/typings/briefs';
import { UserType, VettingStatus } from 'shared/typings/user/enums';
import { LinkCreator } from 'shared/utilities/linkUtility';
import { SIZING } from 'shared/config/formatting';
import ReactHTMLParser from 'react-html-parser';
import { BUTTON_VARIANTS } from 'shared/styles/button';
import { useSubmissions } from 'features/submissions/useSubmissions';
import { SubmissionStatus } from 'shared/typings/submissions';

const { BRIEF_APPLY } = TRACKING;
const ACTIVE_BRIEF_LIMIT = 5;

interface Props {
  userId: number;
  briefId: number;
  className?: string;
  disabled?: boolean;
}

const BriefApplicationButton: React.FC<Props> = ({ userId, briefId, className, disabled }) => {
  const { brief, applyToBrief, applyToBriefActionInProgress } = useBriefs(briefId);
  const { currentUser } = useCurrentUser();
  const [open, setOpen] = useState(false);

  const { briefCreatorStatus } = brief;

  const onCancel = () => {
    setOpen(false);
  };

  const clickHandler = () => {
    if (briefCreatorStatus === UserBriefStatus.NOT_APPLIED) {
      trackEvent(BRIEF_APPLY);
      setOpen(true);
    }
  };

  const onSubmit = (values: { pitch: string; states: { checked: boolean; value: number }[] }) => {
    const statesChosen = (values.states || []).filter(({ checked }) => checked).map(({ value }) => value);
    applyToBrief(briefId, values.pitch, statesChosen);
  };

  const showPromptButton = ((currentUser as CreatorUser)?.inProgressBriefsCount || 0) >= ACTIVE_BRIEF_LIMIT;
  const showPendingCreatorButton = [VettingStatus.PENDING, VettingStatus.NEW].includes(currentUser.vettingStatus);
  // Creators onboarded before the picture was required are gated here rather than locked out.
  const missingProfilePicture = currentUser.type === UserType.CREATOR && !currentUser.profilePicId;

  if (showPendingCreatorButton) {
    return <PendingCreatorButton className={className} />;
  }

  if (missingProfilePicture) {
    return <MissingProfilePictureButton className={className} />;
  }

  return (
    <>
      {showPromptButton ? (
        <MaxReachedButton brief={brief} disabled={disabled} loading={applyToBriefActionInProgress} />
      ) : (
        <ActionButton
          onClick={clickHandler}
          loading={applyToBriefActionInProgress}
          className={className}
          disabled={brief.full || disabled}
          dataCy="brief-application-button"
        >
          {BRIEF_COPY.BUTTON_APPLY_BRIEF}
        </ActionButton>
      )}
      <CreatorBriefStartingSteps brief={brief} userId={userId} onSubmit={onSubmit} onCancel={onCancel} open={open} />
    </>
  );
};

const MaxReachedButton: React.FC<{ brief: Brief; disabled?: boolean; loading: boolean; className?: string }> = ({
  brief,
  loading,
  disabled,
  className,
}) => {
  return (
    <PromptButton
      confirmButtonText="Okay"
      onConfirm={noOp}
      showCancelButton={false}
      className={className}
      disabled={brief.full || disabled}
      loading={loading}
      dataCy="brief-application-button"
      text={BRIEF_COPY.PROMPT_ACTIVE_BRIEF_LIMIT}
    >
      {BRIEF_COPY.BUTTON_APPLY_BRIEF}
    </PromptButton>
  );
};

const MissingProfilePictureButton: React.FC<{ className?: string }> = ({ className }) => {
  const navigate = useNavigate();
  const { currentUser } = useCurrentUser();

  const onConfirm = () => {
    navigate(
      LinkCreator.createLink({
        userType: UserType.CREATOR,
        routeKey: 'USER_PROFILE',
        variables: {
          userId: currentUser.id,
        },
      }),
    );
  };

  return (
    <PromptButton
      confirmButtonText="Add Photo"
      onConfirm={onConfirm}
      showCancelButton={false}
      className={className}
      variant={BUTTON_VARIANTS.PRIMARY}
      dataCy="brief-application-button"
      text={ReactHTMLParser(BRIEF_COPY.PROMPT_ADD_PROFILE_PICTURE)}
      title={BRIEF_COPY.PROMPT_TITLE_ADD_PROFILE_PICTURE}
      modalSize={SIZING.MEDIUM_WIDE}
    >
      {BRIEF_COPY.BUTTON_APPLY_BRIEF}
    </PromptButton>
  );
};

const PendingCreatorButton: React.FC<{ className?: string }> = ({ className }) => {
  const navigate = useNavigate();
  // system brief submission is fetched in CreatorRoutes
  const { submissions, loading } = useSubmissions();

  const hasSubmittedToSystemBrief = submissions.length > 0 && submissions[0].status === SubmissionStatus.SUBMITTED;

  const onSubmit = () => {
    //  navigate to the onboarding brief
    navigate(
      LinkCreator.createLink({
        userType: UserType.CREATOR,
        routeKey: 'CREATOR_SUBMISSIONS',
      }),
    );
  };

  if (hasSubmittedToSystemBrief) {
    return (
      <PromptButton
        confirmButtonText="Close"
        onConfirm={() => {}}
        showCancelButton={false}
        className={className}
        variant={BUTTON_VARIANTS.PRIMARY}
        dataCy="brief-application-button"
        text={BRIEF_COPY.PROMPT_WAIT_FOR_APPROVAL}
        title={BRIEF_COPY.PROMPT_TITLE_WAIT_FOR_APPROVAL}
        modalSize={SIZING.MEDIUM_WIDE}
        loading={loading}
      >
        {BRIEF_COPY.BUTTON_APPLY_BRIEF}
      </PromptButton>
    );
  }

  return (
    <PromptButton
      confirmButtonText="Complete Brief"
      onConfirm={onSubmit}
      showCancelButton={false}
      className={className}
      variant={BUTTON_VARIANTS.PRIMARY}
      dataCy="brief-application-button"
      text={ReactHTMLParser(BRIEF_COPY.PROMPT_COMPLETE_ONBOARDING)}
      title={BRIEF_COPY.PROMPT_TITLE_COMPLETE_ONBOARDING}
      modalSize={SIZING.MEDIUM_WIDE}
      disabled={loading}
    >
      {BRIEF_COPY.BUTTON_APPLY_BRIEF}
    </PromptButton>
  );
};

export default BriefApplicationButton;
